import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsApi, workflowsApi } from '../lib/api';

interface Client {
  id: string;
  slug: string;
  companyName: string;
  city: string;
  niche: string;
}

interface WorkflowState {
  workflowId: string;
  status: string;
  currentStep: number;
  totalSteps: number;
  blogContent?: {
    title: string;
    html: string;
    metaDescription: string;
    excerpt: string;
  };
  wordpressPostUrl?: string;
  gmbDescription?: string;
  error?: string;
}

type Phase = 'input' | 'phase1-running' | 'phase1-complete' | 'phase2-running' | 'complete' | 'error';

export function NewWorkflowPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<Phase>('input');

  // Form state
  const [selectedClientId, setSelectedClientId] = useState('');
  const [paaText, setPaaText] = useState('');
  const [internalLink, setInternalLink] = useState('');

  // Phase 1 results
  const [workflowId, setWorkflowId] = useState('');
  const [blogUrl, setBlogUrl] = useState('');
  const [blogTitle, setBlogTitle] = useState('');
  const [gmbDescription, setGmbDescription] = useState('');

  // Phase 2 input
  const [shareUrl, setShareUrl] = useState('');

  // Error state
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  async function loadClients() {
    try {
      const response = await clientsApi.list();
      setClients(response.clients);
    } catch (err) {
      console.error('Failed to load clients', err);
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }

  async function startPhase1() {
    if (!selectedClientId || !paaText) {
      setError('Please select a client and enter a PAA question');
      return;
    }

    setError('');
    setPhase('phase1-running');
    setStatusMessage('Starting workflow...');

    try {
      // Start workflow in phase 1 mode (stops after blog creation)
      const response = await workflowsApi.create({
        clientId: selectedClientId,
        paaText,
        internalLinks: internalLink ? [internalLink] : [],
        pauseAfterBlog: true,
      });

      setWorkflowId(response.workflow.workflowId);
      setStatusMessage('Generating content... this may take a few minutes.');

      // Poll for phase 1 completion
      await pollWorkflowStatus(response.workflow.workflowId, 'PAUSED');

    } catch (err) {
      console.error('Failed to start workflow', err);
      setError(err instanceof Error ? err.message : 'Failed to start workflow');
      setPhase('error');
    }
  }

  async function pollWorkflowStatus(wfId: string, targetStatus: string) {
    const maxAttempts = 120; // 10 minutes at 5 second intervals
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const status = await workflowsApi.getStatus(wfId);

        setStatusMessage(`Step ${status.currentStep + 1}/${status.totalSteps}: ${status.currentStepName || 'Processing...'}`);

        if (status.status === targetStatus) {
          // Phase 1 complete
          setBlogUrl(status.wordpressPostUrl || '');
          setBlogTitle(status.blogTitle || '');
          setGmbDescription(status.gmbDescription || '');
          setPhase('phase1-complete');
          return;
        }

        if (status.status === 'FAILED') {
          setError(status.error || 'Workflow failed');
          setPhase('error');
          return;
        }

        if (status.status === 'COMPLETED') {
          // Full completion
          setPhase('complete');
          return;
        }

        // Wait 5 seconds before next poll
        await new Promise(resolve => setTimeout(resolve, 5000));
        attempts++;

      } catch (err) {
        console.error('Failed to get workflow status', err);
        // Continue polling on error
        await new Promise(resolve => setTimeout(resolve, 5000));
        attempts++;
      }
    }

    setError('Workflow timed out');
    setPhase('error');
  }

  async function startPhase2() {
    if (!shareUrl) {
      setError('Please enter the share URL');
      return;
    }

    setError('');
    setPhase('phase2-running');
    setStatusMessage('Continuing workflow with share URL...');

    try {
      // Resume workflow with share URL
      await workflowsApi.resume(workflowId, { shareUrl });

      // Poll for completion
      await pollWorkflowStatus(workflowId, 'COMPLETED');

    } catch (err) {
      console.error('Failed to resume workflow', err);
      setError(err instanceof Error ? err.message : 'Failed to resume workflow');
      setPhase('error');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">New Workflow</h1>
        <p className="text-gray-600">Generate content for a PAA question</p>
      </div>

      {/* Phase 1: Input Form */}
      {phase === 'input' && (
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Client
            </label>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Choose a client...</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.companyName} ({client.city})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              PAA Question
            </label>
            <textarea
              value={paaText}
              onChange={(e) => setPaaText(e.target.value)}
              placeholder="e.g., What should I expect during a dental cleaning?"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Internal Link (optional)
            </label>
            <input
              type="url"
              value={internalLink}
              onChange={(e) => setInternalLink(e.target.value)}
              placeholder="https://example.com/related-page"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-sm text-gray-500 mt-1">
              This link will be included in the blog content
            </p>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            onClick={startPhase1}
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Start Workflow
          </button>
        </div>
      )}

      {/* Phase 1: Running */}
      {phase === 'phase1-running' && (
        <div className="bg-white rounded-lg shadow p-6 text-center space-y-4">
          <div className="animate-spin w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full mx-auto"></div>
          <div>
            <p className="font-medium text-gray-900">Generating Content</p>
            <p className="text-sm text-gray-500">{statusMessage}</p>
          </div>
        </div>
      )}

      {/* Phase 1: Complete - Show Results */}
      {phase === 'phase1-complete' && (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-medium text-green-800">Phase 1 Complete!</h3>
            <p className="text-sm text-green-600">Blog post created. Review the content below.</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">Blog Post</h3>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                <strong>Title:</strong> {blogTitle}
              </p>
              <p className="text-sm text-gray-600">
                <strong>URL:</strong>{' '}
                <a
                  href={blogUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline"
                >
                  {blogUrl}
                </a>
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">GMB Post Description</h3>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{gmbDescription}</p>
            </div>
            <button
              onClick={() => navigator.clipboard.writeText(gmbDescription)}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Copy to clipboard
            </button>
          </div>

          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">Continue Workflow</h3>
            <p className="text-sm text-gray-600">
              Enter the share URL that should appear in the podcast, video, and social media posts.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Share URL
              </label>
              <input
                type="url"
                value={shareUrl}
                onChange={(e) => setShareUrl(e.target.value)}
                placeholder={blogUrl || 'https://example.com/your-post'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">
                This URL will be used in podcast description, video, and social posts
              </p>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              onClick={startPhase2}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              Continue to Podcast & Video
            </button>
          </div>
        </div>
      )}

      {/* Phase 2: Running */}
      {phase === 'phase2-running' && (
        <div className="bg-white rounded-lg shadow p-6 text-center space-y-4">
          <div className="animate-spin w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full mx-auto"></div>
          <div>
            <p className="font-medium text-gray-900">Generating Media</p>
            <p className="text-sm text-gray-500">{statusMessage}</p>
          </div>
        </div>
      )}

      {/* Complete */}
      {phase === 'complete' && (
        <div className="bg-white rounded-lg shadow p-6 text-center space-y-4">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p className="font-medium text-gray-900">Workflow Complete!</p>
            <p className="text-sm text-gray-500">All content has been generated and published.</p>
          </div>
          <div className="pt-4 space-x-4">
            <button
              onClick={() => navigate('/workflows')}
              className="px-4 py-2 text-primary-600 hover:text-primary-700"
            >
              View History
            </button>
            <button
              onClick={() => {
                setPhase('input');
                setPaaText('');
                setInternalLink('');
                setShareUrl('');
                setWorkflowId('');
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Start Another
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {phase === 'error' && (
        <div className="bg-white rounded-lg shadow p-6 text-center space-y-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <div>
            <p className="font-medium text-gray-900">Workflow Failed</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
          <button
            onClick={() => {
              setPhase('input');
              setError('');
            }}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}
